import {
  Checkbox,
  Grid,
  GridColumn,
  GridRow,
  ModalBottomButtons,
  Select,
  SelectOptionType,
  ValueField,
} from '@bp/ui-components';
import { Formik, FormikHelpers } from 'formik';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-router-dom';
import { SingleValue } from 'react-select';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import {
  SubstitutionAppFormType,
  SubstitutionAppTimeValues,
} from '../../../pages/Substitutions/Setup/SubstitutionAppSettings/SubstitutionAppSettingsPage';
import {
  useSaveSubstitutionAppConfigMutation,
  useSubstitutionAppConfigQuery,
} from '../../../types/planung-graphql-client-defs';
import { showErrorToast, showSuccessUpdateToast } from '../../../utils/toast';

type SubstitutionAppOverviewFormProps = {
  readonly?: boolean;
  onLoad?: (isLoading: boolean) => void;
  onClose?: () => void;
};

export const SubstitutionAppOverviewForm = ({ readonly, onLoad, onClose }: SubstitutionAppOverviewFormProps) => {
  const { t } = useTranslation();
  const context = useMemorizedCacheTag('SUBSTITUTION_APP_CONFIG');

  const [, save] = useSaveSubstitutionAppConfigMutation();

  const [{ data: appConfigData }] = useSubstitutionAppConfigQuery({ context });
  const appAccess = appConfigData?.substitutionAppConfig;

  const initialValues: SubstitutionAppFormType = {
    teacherPassword: appAccess?.teacherPassword ?? '',
    studentPassword: appAccess?.studentPassword ?? '',
    allowIndividualLogins: appAccess?.allowIndividualLogins ?? false,
    useTeacherShortname: appAccess?.useTeacherShortname ?? true,
    useClassShortname: appAccess?.useClassShortname ?? true,
    useRoomShortname: appAccess?.useRoomShortname ?? true,
    timeDisplayMode: (appAccess?.timeDisplayMode as SubstitutionAppTimeValues) ?? 'time',
    showClassGroups: appAccess?.showClassGroups ?? false,
    showOriginalSubject: appAccess?.showOriginalSubject ?? true,
    showCurrentSubject: appAccess?.showCurrentSubject ?? true,
    showHolidays: appAccess?.showHolidays ?? true,
  };

  const timeOptions: SelectOptionType[] = [
    {
      value: 'time',
      label: `${t('common.from')} - ${t('common.until')}`,
    },
    {
      value: 'lessonName',
      label: t('substitutions.app.lessonName'),
    },
  ];

  async function handleSubmit(values: SubstitutionAppFormType, formHelpers: FormikHelpers<SubstitutionAppFormType>) {
    onLoad && onLoad(true);

    // Beide Formulare der App-Einstellungen schicken immer alle Felder; das Backend legt an oder ersetzt.
    const result = await save({ input: { ...values } }, context);

    if (result.error) {
      showErrorToast(result.error);
    } else {
      showSuccessUpdateToast();
    }

    onLoad && onLoad(false);
    formHelpers.resetForm();
    onClose && onClose();
  }

  return (
    <Formik<SubstitutionAppFormType> initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize>
      {({ setFieldValue, values, isSubmitting, dirty, resetForm, submitForm }) => {
        return (
          <Form>
            <Grid>
              <GridRow>
                <GridColumn width={4}>
                  <ValueField title={t('persons.title', { count: 2 })} mode='headline' position='top'>
                    <Checkbox
                      name={readonly ? 'teacher-shortnames-readonly' : 'teacher-shortnames'}
                      checked={values.useTeacherShortname}
                      onChange={async (e) => {
                        await setFieldValue('useTeacherShortname', e.target.checked);
                      }}
                      label={t('substitutions.settings.showShortnames')}
                      readonly={readonly}
                    />
                  </ValueField>
                </GridColumn>
                <GridColumn width={4}>
                  <ValueField title={t('classes.title', { count: 2 })} mode='headline' position='top'>
                    <Checkbox
                      name={readonly ? 'class-shortnames-readonly' : 'class-shortnames'}
                      checked={values.useClassShortname}
                      onChange={async (e) => {
                        await setFieldValue('useClassShortname', e.target.checked);
                      }}
                      label={t('substitutions.settings.showShortnames')}
                      readonly={readonly}
                    />
                  </ValueField>
                </GridColumn>
                <GridColumn width={4}>
                  <ValueField title={t('substitutions.app.classGroups')} mode='headline' position='top'>
                    <Checkbox
                      name={readonly ? 'show-class-groups-readonly' : 'show-class-groups'}
                      checked={values.showClassGroups}
                      onChange={async (e) => {
                        await setFieldValue('showClassGroups', e.target.checked);
                      }}
                      label={t('common.show')}
                      readonly={readonly}
                    />
                  </ValueField>
                </GridColumn>
              </GridRow>

              <GridRow>
                <GridColumn width={4}>
                  <ValueField title={t('substitutions.app.originSubjects')} mode='headline' position='top'>
                    <Checkbox
                      name={readonly ? 'origin-subject-shortnames-readonly' : 'origin-subject-shortnames'}
                      checked={values.showOriginalSubject}
                      onChange={async (e) => {
                        await setFieldValue('showOriginalSubject', e.target.checked);
                      }}
                      label={t('substitutions.settings.showShortnames')}
                      readonly={readonly}
                    />
                  </ValueField>
                </GridColumn>
                <GridColumn width={4}>
                  <ValueField title={t('substitutions.app.newSubjects')} mode='headline' position='top'>
                    <Checkbox
                      name={readonly ? 'new-subject-shortnames-readonly' : 'new-subject-shortnames'}
                      checked={values.showCurrentSubject}
                      onChange={async (e) => {
                        await setFieldValue('showCurrentSubject', e.target.checked);
                      }}
                      label={t('substitutions.settings.showShortnames')}
                      readonly={readonly}
                    />
                  </ValueField>
                </GridColumn>
                <GridColumn width={4}>
                  <ValueField title={t('rooms.title', { count: 2 })} mode='headline' position='top'>
                    <Checkbox
                      name={readonly ? 'room-shortnames-readonly' : 'room-shortnames'}
                      checked={values.useRoomShortname}
                      onChange={async (e) => {
                        await setFieldValue('useRoomShortname', e.target.checked);
                      }}
                      label={t('substitutions.settings.showShortnames')}
                      readonly={readonly}
                    />
                  </ValueField>
                </GridColumn>
              </GridRow>

              <GridRow>
                <GridColumn width={4}>
                  <ValueField title={t('substitutions.settings.showHolidays')} mode='headline' position='top'>
                    <Checkbox
                      name={readonly ? 'show-holidays-readonly' : 'show-holidays'}
                      checked={values.showHolidays}
                      onChange={async (e) => {
                        await setFieldValue('showHolidays', e.target.checked);
                      }}
                      label={t('common.show')}
                      readonly={readonly}
                    />
                  </ValueField>
                </GridColumn>
              </GridRow>

              <GridRow>
                <GridColumn width={4} offset='right'>
                  <ValueField title={t('substitutions.app.time')} mode='headline' position='top' className='w-100'>
                    <Select
                      options={timeOptions}
                      value={timeOptions.find((o) => o.value === values.timeDisplayMode)}
                      onChange={async (option) => {
                        const opt = option as SingleValue<SelectOptionType>;
                        await setFieldValue('timeDisplayMode', opt?.value);
                      }}
                      menuPosition='fixed'
                      readonly={readonly}
                      name={readonly ? 'times-readonly' : 'times'}
                      dense
                    />
                  </ValueField>
                </GridColumn>
              </GridRow>
            </Grid>

            {!readonly && (
              <ModalBottomButtons
                closeButton={{
                  callback: () => {
                    onClose && onClose();
                    resetForm();
                  },
                }}
                submitButton={{
                  callback: async () => {
                    await submitForm();
                  },
                  disabled: isSubmitting || !dirty,
                }}
              />
            )}
          </Form>
        );
      }}
    </Formik>
  );
};
